import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Task, TaskStatus } from '../types/Task';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { Scorecard } from '../components/Scorecard';
import { TaskTable } from '../components/TaskTable';
import { TaskCard } from '../components/TaskCard';
import { TaskBoard } from '../components/TaskBoard';
import { TaskVirtualizedList } from '../components/TaskVirtualizedList';
import { SearchBar } from '../components/SearchBar';
import { Pagination } from '../components/Pagination';
import { ViewModeToggle } from '../components/ViewModeToggle';
import LoginModal from '../components/LoginModal';
import TaskDetailModal from '../components/TaskDetailModal';
import TaskShadowWidget from '../components/TaskShadowWidget';
import { TaskContextMenu } from '../components/TaskContextMenu';
import TaskFormModal, { TaskFormSaveResult } from '../components/TaskFormModal';
import ToastStack, { TaskToast, ToastVariant } from '../components/ToastStack';
import './TaskManagerPage.css';

interface TaskContextMenuState {
  task: Task;
  x: number;
  y: number;
}

const BOARD_PAGE_SIZE = 1000;
const MAX_TOASTS = 3;
const TOAST_DURATION_MS: Record<ToastVariant, number> = {
  success: 4000,
  info: 4000,
  error: 6000,
};

const formatTaskStatus = (status: string) => {
  switch (status) {
    case TaskStatus.TODO:
      return 'TODO';
    case TaskStatus.IN_PROGRESS:
      return 'In Progress';
    case TaskStatus.DONE:
      return 'Done';
    default:
      return status;
  }
};

const formatTaskToastMessage = ({ action, taskTitle }: TaskFormSaveResult) => {
  const quotedTitle = taskTitle ? `"${taskTitle}"` : 'Task';

  switch (action) {
    case 'created':
      return taskTitle ? `Created ${quotedTitle}.` : 'Task created.';
    case 'updated':
      return taskTitle ? `Updated ${quotedTitle}.` : 'Task updated.';
    case 'deleted':
      return taskTitle ? `Deleted ${quotedTitle}.` : 'Task deleted.';
    default:
      return 'Task updated.';
  }
};

const CONTEXT_MENU_COPY_SUFFIX = ' (copy)';
const TRAINING_PANEL_QUERY_KEY = 'panel';
const HELP_PANEL_QUERY_VALUE = 'help';
const SHADOW_PANEL_QUERY_VALUE = 'shadow';
const TASK_MANAGER_RELEASE_NOTES_IFRAME_SRC = '/task-manager-help.html';
const LIST_DATA_MODE_QUERY_KEY = 'listMode';
const LIST_RENDER_MODE_QUERY_KEY = 'listRender';

type TaskListDataMode = 'paged' | 'infinite';
type TaskListRenderMode = 'standard' | 'virtualized';
type TrainingPanelMode = typeof HELP_PANEL_QUERY_VALUE | typeof SHADOW_PANEL_QUERY_VALUE;

const resolveTrainingPanelMode = (value: string | null): TrainingPanelMode | null => {
  if (value === HELP_PANEL_QUERY_VALUE || value === SHADOW_PANEL_QUERY_VALUE) {
    return value;
  }

  return null;
};

const buildDuplicateTaskPrefill = (task: Task): Task => ({
  title: task.title.endsWith(CONTEXT_MENU_COPY_SUFFIX) ? task.title : `${task.title}${CONTEXT_MENU_COPY_SUFFIX}`,
  description: task.description || '',
  status: task.status,
  priority: task.priority,
  dueDate: task.dueDate,
  labels: [...(task.labels || [])],
  checklistItems: (task.checklistItems || []).map((item) => ({
    text: item.text,
    done: item.done,
  })),
});

const formatTaskSummaryAlertMessage = (task: Task) => [
  `Title: ${task.title}`,
  `Status: ${formatTaskStatus(task.status)}`,
  `Priority: ${task.priority}`,
  `Due date: ${task.dueDate || 'No due date'}`,
].join('\n');

const TASK_LIST_SKELETON_ROWS = 5;

function TaskListSkeleton() {
  return (
    <div
      className="task-list-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading tasks"
      data-testid="task-list-skeleton"
    >
      <div className="task-list-skeleton-table" aria-hidden="true">
        <div className="task-list-skeleton-row task-list-skeleton-row--header">
          <span className="task-skeleton-block task-skeleton-block--checkbox" />
          <span className="task-skeleton-block task-skeleton-block--title" />
          <span className="task-skeleton-block task-skeleton-block--meta" />
          <span className="task-skeleton-block task-skeleton-block--meta" />
          <span className="task-skeleton-block task-skeleton-block--date" />
          <span className="task-skeleton-block task-skeleton-block--actions" />
        </div>

        {Array.from({ length: TASK_LIST_SKELETON_ROWS }, (_, index) => (
          <div key={index} className="task-list-skeleton-row">
            <span className="task-skeleton-block task-skeleton-block--checkbox" />
            <span className="task-skeleton-block task-skeleton-block--title" />
            <span className="task-skeleton-block task-skeleton-block--meta" />
            <span className="task-skeleton-block task-skeleton-block--meta" />
            <span className="task-skeleton-block task-skeleton-block--date" />
            <span className="task-skeleton-block task-skeleton-block--actions" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Task Manager Page - Full implementation with CRUD operations
 * Features: Scorecards, Search, Filter, Sortable Table, Mobile Cards, Pagination, Multi-select, Bulk Operations
 */
const TaskManagerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const nextToastId = React.useRef(0);
  const toastTimeouts = React.useRef<number[]>([]);
  const isMountedRef = React.useRef(true);
  const { user, isAuthenticated, login, logout, apiVersion, setApiVersion } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<TaskToast[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskContextMenu, setTaskContextMenu] = useState<TaskContextMenuState | null>(null);
  const [taskFormPrefillTask, setTaskFormPrefillTask] = useState<Task | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const [taskDetailLoading, setTaskDetailLoading] = useState(false);
  const [taskDetailError, setTaskDetailError] = useState<string | null>(null);

  const modalParams = new URLSearchParams(location.search);
  const taskModalMode = modalParams.get('taskModal');
  const taskModalId = modalParams.get('taskId');
  const viewMode = modalParams.get('view') === 'board' ? 'board' : 'list';
  const listDataMode: TaskListDataMode = modalParams.get(LIST_DATA_MODE_QUERY_KEY) === 'infinite' ? 'infinite' : 'paged';
  const listRenderMode: TaskListRenderMode = modalParams.get(LIST_RENDER_MODE_QUERY_KEY) === 'virtualized' ? 'virtualized' : 'standard';
  const isInfiniteListMode = viewMode === 'list' && listDataMode === 'infinite';
  const isVirtualizedListMode = viewMode === 'list' && listRenderMode === 'virtualized';
  const activeTrainingPanel = resolveTrainingPanelMode(modalParams.get(TRAINING_PANEL_QUERY_KEY));
  const isHelpPanelOpen = activeTrainingPanel === HELP_PANEL_QUERY_VALUE;
  const isShadowPanelOpen = activeTrainingPanel === SHADOW_PANEL_QUERY_VALUE;
  const viewingTaskId = taskModalMode === 'detail' && taskModalId ? Number(taskModalId) : undefined;
  const editingTaskId = taskModalMode === 'edit' && taskModalId ? Number(taskModalId) : undefined;
  const isTaskFormOpen = taskModalMode === 'create' || (taskModalMode === 'edit' && Number.isFinite(editingTaskId));
  const editingTask = typeof editingTaskId === 'number'
    ? tasks.find((task) => task.id === editingTaskId) ?? null
    : null;
  
  // Multi-select state
  const [selectedTasks, setSelectedTasks] = useState<Set<number>>(new Set());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasMoreTasks, setHasMoreTasks] = useState(false);
  const [loadingMoreTasks, setLoadingMoreTasks] = useState(false);
  const infiniteScrollSentinelRef = React.useRef<HTMLDivElement | null>(null);
  
  // Scorecard counts
  const [taskCounts, setTaskCounts] = useState({
    total: 0,
    todo: 0,
    inProgress: 0,
    done: 0
  });
  const queryDrivenDetailTaskId = React.useRef<number | null>(null);

  const closeTaskContextMenu = () => {
    setTaskContextMenu(null);
  };

  const openTaskContextMenu = (task: Task, x: number, y: number) => {
    setTaskContextMenu({ task, x, y });
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      toastTimeouts.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      toastTimeouts.current = [];
    };
  }, []);

  const dismissToast = (toastId: number) => {
    if (!isMountedRef.current) {
      return;
    }

    setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== toastId));
  };

  const showToast = (variant: ToastVariant, message: string, action?: TaskToast['action']) => {
    if (!isMountedRef.current) {
      return;
    }

    nextToastId.current += 1;
    const toastId = nextToastId.current;

    setToasts((currentToasts) => ([
      { id: toastId, variant, message, action },
      ...currentToasts,
    ].slice(0, MAX_TOASTS)));

    const timeoutId = window.setTimeout(() => {
      if (!isMountedRef.current) {
        toastTimeouts.current = toastTimeouts.current.filter((entry) => entry !== timeoutId);
        return;
      }

      dismissToast(toastId);
      toastTimeouts.current = toastTimeouts.current.filter((entry) => entry !== timeoutId);
    }, TOAST_DURATION_MS[variant]);

    toastTimeouts.current.push(timeoutId);
  };

  // Update API version when it changes
  useEffect(() => {
    apiService.setApiVersion(apiVersion);
  }, [apiVersion]);

  useEffect(() => {
    if (apiVersion === 'v2' && !isAuthenticated) {
      return;
    }

    fetchTaskSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiVersion, isAuthenticated]);

  const handleApiVersionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const version = e.target.value as 'v1' | 'v2' | 'buggy';
    setApiVersion(version);
    
    // If switching to V2 and not authenticated, open login modal
    if (version === 'v2' && !isAuthenticated) {
      setIsLoginModalOpen(true);
      return;
    }
  };

  const handleLogin = async (username: string, password: string) => {
    await login(username, password);
    if (!isMountedRef.current) {
      return;
    }

    showToast('success', 'Logged in to API V2.');
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      const result = await logout();
      if (!isMountedRef.current) {
        return;
      }

      if (result.localOnly) {
        showToast('info', 'Logged out locally. Server notification may have failed.');
        return;
      }

      showToast('success', 'Logged out.');
    }
  };

  useEffect(() => {
    if (apiVersion === 'v2' && !isAuthenticated) {
      return;
    }

    const shouldAppend = isInfiniteListMode && currentPage > 0 && viewMode === 'list';
    void fetchTasks({
      append: shouldAppend,
      targetPage: currentPage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchQuery, selectedStatus, viewMode, listDataMode, apiVersion, isAuthenticated]);

  useEffect(() => {
    if (viewMode === 'board' && selectedTasks.size > 0) {
      setSelectedTasks(new Set());
    }
  }, [viewMode, selectedTasks]);

  const handleRetryTaskLoading = async () => {
    await Promise.all([fetchTasks(), fetchTaskSummary()]);
  };

  const fetchTasks = async (options: { append?: boolean; targetPage?: number } = {}) => {
    if (!isMountedRef.current) {
      return false;
    }

    const shouldAppend = Boolean(options.append && isInfiniteListMode && viewMode === 'list');
    const targetPage = options.targetPage ?? (isInfiniteListMode && !shouldAppend ? 0 : currentPage);

    if (shouldAppend) {
      setLoadingMoreTasks(true);
    } else {
      setLoading(true);
    }

    setError(null);
    try {
      const effectivePage = viewMode === 'board' ? 0 : targetPage;
      const effectivePageSize = viewMode === 'board'
        ? Math.max(totalElements, pageSize, BOARD_PAGE_SIZE)
        : pageSize;
      const response = await apiService.getAllTasks({
        page: effectivePage,
        size: effectivePageSize,
        search: searchQuery || undefined,
        status: selectedStatus !== 'ALL' ? selectedStatus as TaskStatus : undefined
      });

      if (!isMountedRef.current) {
        return false;
      }

      if (shouldAppend) {
        setTasks((existingTasks) => {
          const knownTaskIds = new Set(existingTasks.map((task) => task.id));
          const appendedTasks = response.content.filter((task) => !knownTaskIds.has(task.id));
          return [...existingTasks, ...appendedTasks];
        });
      } else {
        setTasks(response.content);
        setSelectedTasks(new Set());
      }

      setCurrentPage(response.currentPage);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
      setHasMoreTasks(response.currentPage + 1 < response.totalPages);
      return true;
    } catch (err) {
      if (isMountedRef.current) {
        setError('Failed to load tasks. Please try again.');
      }
      console.error(err);
      return false;
    } finally {
      if (!isMountedRef.current) {
        return;
      }

      if (shouldAppend) {
        setLoadingMoreTasks(false);
      } else {
        setLoading(false);
      }
    }
  };

  const fetchTaskSummary = async () => {
    try {
      const summary = await apiService.getTaskSummary();
      if (!isMountedRef.current) {
        return false;
      }

      setTaskCounts({
        total: summary.totalTasks,
        todo: summary.todoTasks,
        inProgress: summary.inProgressTasks,
        done: summary.doneTasks
      });
      return true;
    } catch (err) {
      console.error('Failed to fetch task summary:', err);
      return false;
    }
  };

  const refreshSelectedTask = async () => {
    if (!selectedTask?.id || !isMountedRef.current) {
      return;
    }

    setTaskDetailLoading(true);
    setTaskDetailError(null);

    try {
      const taskDetails = await apiService.getTaskById(selectedTask.id);
      if (!isMountedRef.current) {
        return;
      }

      setSelectedTask(taskDetails);
    } catch (err) {
      if (isMountedRef.current) {
        setTaskDetailError('Failed to load task details. Please try again.');
      }
      console.error(err);
    } finally {
      if (isMountedRef.current) {
        setTaskDetailLoading(false);
      }
    }
  };

  const openTaskDetails = async (task: Task) => {
    closeTaskContextMenu();

    if (!task.id) {
      setSelectedTask(task);
      setIsTaskDetailOpen(true);
      setTaskDetailLoading(false);
      setTaskDetailError('Task details are unavailable.');
      return;
    }

    await openTaskDetailsById(task.id, task);
  };

  const openTaskDetailsById = async (taskId: number, previewTask?: Task | null) => {
    if (!isMountedRef.current) {
      return;
    }

    setSelectedTask(previewTask ?? null);
    setIsTaskDetailOpen(true);
    setTaskDetailLoading(true);
    setTaskDetailError(null);
    updateTaskModalQuery('detail', taskId);

    try {
      const taskDetails = await apiService.getTaskById(taskId);
      if (!isMountedRef.current) {
        return;
      }

      setSelectedTask(taskDetails);
    } catch (err) {
      if (isMountedRef.current) {
        setTaskDetailError('Failed to load task details. Please try again.');
      }
      console.error(err);
    } finally {
      if (isMountedRef.current) {
        setTaskDetailLoading(false);
      }
    }
  };

  const buildTaskDetailModalUrl = (taskId: number) => {
    return `${window.location.pathname}/details/${taskId}`;
  };

  const updateTaskModalQuery = (mode?: 'create' | 'edit' | 'detail', taskId?: number) => {
    const nextParams = new URLSearchParams(location.search);

    nextParams.delete('taskModal');
    nextParams.delete('taskId');

    if (mode) {
      nextParams.set('taskModal', mode);
      if (typeof taskId === 'number') {
        nextParams.set('taskId', String(taskId));
      }
    }

    navigate(
      {
        pathname: '/',
        search: nextParams.toString() ? `?${nextParams.toString()}` : '',
      },
      { replace: true }
    );
  };

  const openTaskDetailsInNewTab = (task: Task) => {
    closeTaskContextMenu();

    if (!task.id) {
      return;
    }

    window.open(buildTaskDetailModalUrl(task.id), '_blank');
  };

  const closeTaskDetails = () => {
    closeTaskContextMenu();
    if (taskModalMode === 'detail') {
      queryDrivenDetailTaskId.current = null;
      updateTaskModalQuery(undefined);
    }
    setIsTaskDetailOpen(false);
    setSelectedTask(null);
    setTaskDetailLoading(false);
    setTaskDetailError(null);
  };

  const createComment = async (text: string) => {
    if (!selectedTask?.id) {
      throw new Error('Task details are unavailable.');
    }

    await apiService.addComment(selectedTask.id, text);
    await Promise.all([refreshSelectedTask(), fetchTasks()]);
  };

  const updateComment = async (commentId: number, text: string) => {
    if (!selectedTask?.id) {
      throw new Error('Task details are unavailable.');
    }

    await apiService.updateComment(selectedTask.id, commentId, text);
    await Promise.all([refreshSelectedTask(), fetchTasks()]);
  };

  const deleteComment = async (commentId: number) => {
    if (!selectedTask?.id) {
      throw new Error('Task details are unavailable.');
    }

    await apiService.deleteComment(selectedTask.id, commentId);
    await Promise.all([refreshSelectedTask(), fetchTasks()]);
  };

  // Remove filterTasks function - filtering now done by API

  const handleDeleteTask = async (id: number) => {
    const taskTitle = tasks.find((task) => task.id === id)?.title;
    setError(null);
    try {
      await apiService.deleteTask(id);

      const [tasksRefreshed] = await Promise.all([fetchTasks(), fetchTaskSummary()]);
      if (tasksRefreshed) {
        showToast('success', formatTaskToastMessage({ action: 'deleted', taskTitle }));
      }
    } catch (err) {
      showToast('error', 'Failed to delete task. Please try again.');
      console.error(err);
    }
  };

  const handleStatusChange = async (id: number, status: string) => {
    const task = tasks.find((t) => t.id === id);
    if (!task || task.status === status) return;

    setError(null);
    try {
      await apiService.updateTask(id, {
        ...task,
        status: status as Task['status']
      });

      const [tasksRefreshed] = await Promise.all([fetchTasks(), fetchTaskSummary()]);
      if (tasksRefreshed) {
        showToast(
          'success',
          task.title
            ? `Marked "${task.title}" as ${formatTaskStatus(status)}.`
            : `Task status updated to ${formatTaskStatus(status)}.`
        );
      }
    } catch (err) {
      showToast('error', 'Failed to update task status. Please try again.');
      console.error(err);
    }
  };

  const handleReorderTask = async (id: number, targetStatus: TaskStatus, targetIndex: number) => {
    const task = tasks.find((entry) => entry.id === id);
    if (!task) {
      return;
    }

    setError(null);
    try {
      await apiService.reorderTask(id, targetStatus, targetIndex);

      const [tasksRefreshed] = await Promise.all([fetchTasks(), fetchTaskSummary()]);
      if (tasksRefreshed) {
        showToast(
          'success',
          task.status === targetStatus
            ? `Reordered "${task.title}" within ${formatTaskStatus(targetStatus)}.`
            : `Moved "${task.title}" to ${formatTaskStatus(targetStatus)}.`
        );
      }
    } catch (err) {
      showToast('error', 'Failed to reorder task. Please try again.');
      console.error(err);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(0); // Reset to first page on search
  };

  const handleStatusFilter = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(0); // Reset to first page on filter
  };

  const handleScorecardClick = (status: string) => {
    // Toggle filter: if clicking the same status, reset to ALL, otherwise filter by status
    if (selectedStatus === status) {
      setSelectedStatus('ALL');
    } else {
      setSelectedStatus(status);
    }
    setCurrentPage(0); // Reset to first page
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0); // Reset to first page
  };

  const handleLoadMoreTasks = React.useCallback(() => {
    if (loading || loadingMoreTasks || !hasMoreTasks) {
      return;
    }

    setCurrentPage((previousPage) => previousPage + 1);
  }, [hasMoreTasks, loading, loadingMoreTasks]);

  const updateListModeQuery = (nextDataMode: TaskListDataMode, nextRenderMode: TaskListRenderMode) => {
    const nextParams = new URLSearchParams(location.search);

    if (nextDataMode === 'paged') {
      nextParams.delete(LIST_DATA_MODE_QUERY_KEY);
    } else {
      nextParams.set(LIST_DATA_MODE_QUERY_KEY, 'infinite');
    }

    if (nextRenderMode === 'standard') {
      nextParams.delete(LIST_RENDER_MODE_QUERY_KEY);
    } else {
      nextParams.set(LIST_RENDER_MODE_QUERY_KEY, 'virtualized');
    }

    navigate(
      {
        pathname: '/',
        search: nextParams.toString() ? `?${nextParams.toString()}` : '',
      },
      { replace: true }
    );
  };

  const handleListDataModeChange = (nextDataMode: TaskListDataMode) => {
    if (nextDataMode === listDataMode) {
      return;
    }

    closeTaskContextMenu();
    setCurrentPage(0);
    updateListModeQuery(nextDataMode, listRenderMode);
  };

  const handleListRenderModeChange = (nextRenderMode: TaskListRenderMode) => {
    if (nextRenderMode === listRenderMode) {
      return;
    }

    closeTaskContextMenu();
    updateListModeQuery(listDataMode, nextRenderMode);
  };

  // Multi-select handlers
  const handleSelectTask = (id: number) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedTasks(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTasks.size === tasks.length) {
      // Deselect all
      setSelectedTasks(new Set());
    } else {
      // Select all on current page
      const allIds = new Set(tasks.map(task => task.id!));
      setSelectedTasks(allIds);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTasks.size === 0) return;
    const selectedCount = selectedTasks.size;
    
    const confirmMessage = `Are you sure you want to delete ${selectedCount} task(s)?`;
    if (!window.confirm(confirmMessage)) return;

    setError(null);
    try {
      // Delete each selected task
      await Promise.all(
        Array.from(selectedTasks).map(id => apiService.deleteTask(id))
      );
      
      // Clear selection and refresh
      setSelectedTasks(new Set());

      const [tasksRefreshed] = await Promise.all([fetchTasks(), fetchTaskSummary()]);
      if (tasksRefreshed) {
        showToast('success', `Deleted ${selectedCount} task${selectedCount === 1 ? '' : 's'}.`);
      }
    } catch (err) {
      showToast('error', 'Failed to delete selected tasks. Please try again.');
      console.error(err);
    }
  };

  const handleBulkStatusUpdate = async (newStatus: TaskStatus) => {
    if (selectedTasks.size === 0) return;
    const selectedCount = selectedTasks.size;

    setError(null);
    try {
      // Update each selected task
      await Promise.all(
        Array.from(selectedTasks).map(id => {
          const task = tasks.find(t => t.id === id);
          if (task) {
            return apiService.updateTask(id, {
              ...task,
              status: newStatus
            });
          }
          return Promise.resolve();
        })
      );
      
      // Clear selection and refresh
      setSelectedTasks(new Set());

      const [tasksRefreshed] = await Promise.all([fetchTasks(), fetchTaskSummary()]);
      if (tasksRefreshed) {
        showToast(
          'success',
          `Updated ${selectedCount} task${selectedCount === 1 ? '' : 's'} to ${formatTaskStatus(newStatus)}.`
        );
      }
    } catch (err) {
      showToast('error', 'Failed to update selected tasks. Please try again.');
      console.error(err);
    }
  };

  const updateTaskFormQuery = (mode?: 'create' | 'edit', taskId?: number) => {
    updateTaskModalQuery(mode, taskId);
  };

  const handleOpenCreateModal = () => {
    closeTaskContextMenu();
    setTaskFormPrefillTask(null);
    updateTaskFormQuery('create');
  };

  const handleOpenEditModal = (task: Task) => {
    closeTaskContextMenu();
    setTaskFormPrefillTask(null);
    updateTaskFormQuery('edit', task.id);
  };

  const handleOpenDuplicateModal = (task: Task) => {
    closeTaskContextMenu();
    setTaskFormPrefillTask(buildDuplicateTaskPrefill(task));
    updateTaskFormQuery('create');
  };

  const handleOpenDuplicateAsModal = (task: Task) => {
    closeTaskContextMenu();

    const promptedTitle = window.prompt(
      'Duplicate task as:',
      task.title.endsWith(CONTEXT_MENU_COPY_SUFFIX) ? task.title : `${task.title}${CONTEXT_MENU_COPY_SUFFIX}`
    );

    if (promptedTitle === null) {
      return;
    }

    const duplicateTitle = promptedTitle.trim();

    if (!duplicateTitle) {
      return;
    }

    setTaskFormPrefillTask({
      ...buildDuplicateTaskPrefill(task),
      title: duplicateTitle,
    });
    updateTaskFormQuery('create');
  };

  const handleShowTaskSummary = (task: Task) => {
    closeTaskContextMenu();
    window.alert(formatTaskSummaryAlertMessage(task));
  };

  const handleCloseTaskForm = () => {
    closeTaskContextMenu();
    setTaskFormPrefillTask(null);
    updateTaskFormQuery(undefined);
  };

  const updateTrainingPanelQuery = (panel?: TrainingPanelMode) => {
    const nextParams = new URLSearchParams(location.search);

    if (panel) {
      nextParams.set(TRAINING_PANEL_QUERY_KEY, panel);
    } else {
      nextParams.delete(TRAINING_PANEL_QUERY_KEY);
    }

    navigate(
      {
        pathname: '/',
        search: nextParams.toString() ? `?${nextParams.toString()}` : '',
      },
      { replace: true }
    );
  };

  const handleOpenHelpPanel = () => {
    closeTaskContextMenu();
    updateTrainingPanelQuery(HELP_PANEL_QUERY_VALUE);
  };

  const handleCloseHelpPanel = () => {
    updateTrainingPanelQuery(undefined);
  };

  const handleOpenShadowPanel = () => {
    closeTaskContextMenu();
    updateTrainingPanelQuery(SHADOW_PANEL_QUERY_VALUE);
  };

  const handleCloseShadowPanel = () => {
    updateTrainingPanelQuery(undefined);
  };

  const handleViewModeChange = (nextViewMode: 'list' | 'board') => {
    closeTaskContextMenu();
    const nextParams = new URLSearchParams(location.search);

    if (nextViewMode === 'board') {
      nextParams.set('view', 'board');
    } else {
      nextParams.delete('view');
    }

    navigate(
      {
        pathname: '/',
        search: nextParams.toString() ? `?${nextParams.toString()}` : '',
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (viewMode !== 'list') {
      return;
    }

    setCurrentPage(0);
  }, [viewMode, listDataMode, searchQuery, selectedStatus, pageSize]);

  useEffect(() => {
    if (!isInfiniteListMode || isVirtualizedListMode || loading || loadingMoreTasks || !hasMoreTasks || error) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const sentinelElement = infiniteScrollSentinelRef.current;
    if (!sentinelElement) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isSentinelVisible = entries.some((entry) => entry.isIntersecting);

        if (isSentinelVisible && !loadingMoreTasks && hasMoreTasks) {
          observer.disconnect();
          handleLoadMoreTasks();
        }
      },
      { rootMargin: '200px 0px' }
    );

    observer.observe(sentinelElement);

    return () => {
      observer.disconnect();
    };
  }, [isInfiniteListMode, isVirtualizedListMode, loading, loadingMoreTasks, hasMoreTasks, error, tasks.length, handleLoadMoreTasks]);

  useEffect(() => {
    setTaskContextMenu((current) => (current ? null : current));
  }, [isLoginModalOpen, isTaskDetailOpen, isTaskFormOpen, viewMode]);

  useEffect(() => {
    const queryTaskId = viewingTaskId;

    if (taskModalMode === 'detail' && typeof queryTaskId === 'number' && Number.isFinite(queryTaskId)) {
      if (queryDrivenDetailTaskId.current === queryTaskId) {
        return;
      }

      queryDrivenDetailTaskId.current = queryTaskId;
      void openTaskDetailsById(queryTaskId, tasks.find((task) => task.id === queryTaskId) ?? null);
      return;
    }

    if (queryDrivenDetailTaskId.current !== null) {
      queryDrivenDetailTaskId.current = null;
      setIsTaskDetailOpen(false);
      setSelectedTask(null);
      setTaskDetailLoading(false);
      setTaskDetailError(null);
    }
  }, [taskModalMode, tasks, viewingTaskId]);

  const handleTaskFormSaved = async (result: TaskFormSaveResult) => {
    handleCloseTaskForm();
    const [tasksRefreshed] = await Promise.all([fetchTasks(), fetchTaskSummary()]);
    if (!isMountedRef.current) {
      return;
    }

    const createdTaskId = result.action === 'created' ? result.taskId : undefined;

    if (tasksRefreshed) {
      showToast(
        'success',
        formatTaskToastMessage(result),
        typeof createdTaskId === 'number'
          ? {
              label: 'View task',
              onClick: () => {
                void openTaskDetailsById(createdTaskId);
              },
            }
          : undefined
      );
    }
  };

  return (
    <div className="home-page">
      <header className="page-header">
        <div className="header-content">
          <h1>Task Manager</h1>
          <div className="header-controls">
            <div className="api-selector-group">
              <label htmlFor="api-version">API:</label>
              <select 
                id="api-version" 
                value={apiVersion} 
                onChange={handleApiVersionChange}
                className="api-version-select"
              >
                <option value="v1">V1 - Simple (No Auth)</option>
                <option value="v2">V2 - JWT Auth</option>
                <option value="buggy">Buggy - Testing</option>
              </select>
            </div>

            {isAuthenticated ? (
              <div className="user-info-group">
                <span className="user-badge">
                  {user?.username} ({user?.role})
                </span>
                <button onClick={handleLogout} className="btn-logout">
                  Logout
                </button>
              </div>
            ) : (
              apiVersion === 'v2' && (
                <button onClick={() => setIsLoginModalOpen(true)} className="btn-login">
                  Login
                </button>
              )
            )}

            <a
              className="btn btn-secondary"
              href={(() => { const u = process.env.REACT_APP_API_URL || 'http://localhost:8080/api'; const base = u.startsWith('http') ? u.replace(/\/api$/, '') : 'https://api.testauto.app'; return base + '/swagger-ui/index.html'; })()}
              target="_blank"
              rel="noopener noreferrer"
            >
              API Docs
            </a>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleOpenShadowPanel}
              aria-expanded={isShadowPanelOpen}
              aria-controls="task-manager-shadow-panel"
            >
              Shadow DOM Lab
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleOpenHelpPanel}
              aria-expanded={isHelpPanelOpen}
              aria-controls="task-manager-help-panel"
            >
              Release Notes
            </button>
          </div>
        </div>
      </header>

      {isShadowPanelOpen ? (
        <section
          id="task-manager-shadow-panel"
          className="task-shadow-panel"
          aria-label="Shadow DOM training panel"
        >
          <div className="task-shadow-panel-header">
            <div>
              <h2>Shadow DOM Locator Lab</h2>
              <p>Practice host-level assertions and open shadow-root traversal with an isolated training widget that does not change real task fields.</p>
            </div>
            <button
              type="button"
              className="btn btn-secondary task-shadow-panel-close"
              onClick={handleCloseShadowPanel}
            >
              Close shadow lab
            </button>
          </div>
          <TaskShadowWidget />
        </section>
      ) : null}

      {isHelpPanelOpen ? (
        <section
          id="task-manager-help-panel"
          className="task-help-panel"
          aria-label="Task Manager release notes panel"
        >
          <div className="task-help-panel-header">
            <div>
              <h2>Task Manager Release Notes</h2>
              <p>Embedded same-origin release notes for nested-frame automation practice.</p>
            </div>
            <button
              type="button"
              className="btn btn-secondary task-help-panel-close"
              onClick={handleCloseHelpPanel}
            >
              Close release notes
            </button>
          </div>
          <iframe
            className="task-help-iframe"
            src={TASK_MANAGER_RELEASE_NOTES_IFRAME_SRC}
            title="Task Manager Release Notes"
            loading="lazy"
          />
        </section>
      ) : null}

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={handleLogin}
      />

      <TaskDetailModal
        isOpen={isTaskDetailOpen}
        task={selectedTask}
        loading={taskDetailLoading}
        error={taskDetailError}
        onAddComment={createComment}
        onUpdateComment={updateComment}
        onDeleteComment={deleteComment}
        onClose={closeTaskDetails}
      />

      <TaskFormModal
        isOpen={isTaskFormOpen}
        taskId={editingTaskId}
        initialTask={editingTask}
        prefillTask={taskModalMode === 'create' ? taskFormPrefillTask : null}
        onClose={handleCloseTaskForm}
        onSaved={handleTaskFormSaved}
      />

      <TaskContextMenu
        isOpen={Boolean(taskContextMenu)}
        x={taskContextMenu?.x ?? 0}
        y={taskContextMenu?.y ?? 0}
        taskTitle={taskContextMenu?.task.title ?? 'task'}
        onClose={closeTaskContextMenu}
        onViewDetails={() => {
          if (taskContextMenu) {
            void openTaskDetails(taskContextMenu.task);
          }
        }}
        onOpenDetailsInNewTab={() => {
          if (taskContextMenu) {
            openTaskDetailsInNewTab(taskContextMenu.task);
          }
        }}
        onEdit={() => {
          if (taskContextMenu) {
            handleOpenEditModal(taskContextMenu.task);
          }
        }}
        onDuplicate={() => {
          if (taskContextMenu) {
            handleOpenDuplicateModal(taskContextMenu.task);
          }
        }}
        onDuplicateAs={() => {
          if (taskContextMenu) {
            handleOpenDuplicateAsModal(taskContextMenu.task);
          }
        }}
        onShowTaskSummary={() => {
          if (taskContextMenu) {
            handleShowTaskSummary(taskContextMenu.task);
          }
        }}
        onDelete={() => {
          if (taskContextMenu?.task.id && window.confirm('Are you sure you want to delete this task?')) {
            handleDeleteTask(taskContextMenu.task.id);
          }
        }}
      />

      <ToastStack toasts={toasts} onDismiss={dismissToast} />

      {error && (
        <div className="error-banner" role="alert">
          <span>{error}</span>
          <div className="error-banner-actions">
            <button
              type="button"
              className="btn-secondary error-retry-button"
              onClick={() => void handleRetryTaskLoading()}
              disabled={loading}
            >
              {loading ? 'Retrying...' : 'Retry'}
            </button>
            <button
              type="button"
              onClick={() => setError(null)}
              className="error-close"
              aria-label="Dismiss task loading error"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Scorecards Row */}
      <div className="scorecards-container">
        <Scorecard
          title="Total Tasks"
          count={taskCounts.total}
          color="#3498db"
          onClick={() => handleScorecardClick('ALL')}
          isActive={selectedStatus === 'ALL'}
        />
        <Scorecard
          title="TODO"
          count={taskCounts.todo}
          color="#f39c12"
          onClick={() => handleScorecardClick('TODO')}
          isActive={selectedStatus === 'TODO'}
        />
        <Scorecard
          title="In Progress"
          count={taskCounts.inProgress}
          color="#3498db"
          onClick={() => handleScorecardClick('IN_PROGRESS')}
          isActive={selectedStatus === 'IN_PROGRESS'}
        />
        <Scorecard
          title="Done"
          count={taskCounts.done}
          color="#2ecc71"
          onClick={() => handleScorecardClick('DONE')}
          isActive={selectedStatus === 'DONE'}
        />
      </div>

      {/* Action Bar */}
      <div className="action-bar">
        <button className="btn-add-task" onClick={handleOpenCreateModal}>
          + Add New Task
        </button>
        <SearchBar
          searchQuery={searchQuery}
          onSearchChange={handleSearch}
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusFilter}
        />
        {viewMode === 'list' && (
          <div className="list-mode-controls" aria-label="List display controls">
            <div className="list-mode-group" role="group" aria-label="List data mode">
              <span className="list-mode-label">Data</span>
              <button
                type="button"
                className="list-mode-button"
                aria-pressed={listDataMode === 'paged'}
                onClick={() => handleListDataModeChange('paged')}
              >
                Paged
              </button>
              <button
                type="button"
                className="list-mode-button"
                aria-pressed={listDataMode === 'infinite'}
                onClick={() => handleListDataModeChange('infinite')}
              >
                Infinite
              </button>
            </div>

            <div className="list-mode-group" role="group" aria-label="List render mode">
              <span className="list-mode-label">Render</span>
              <button
                type="button"
                className="list-mode-button"
                aria-pressed={listRenderMode === 'standard'}
                onClick={() => handleListRenderModeChange('standard')}
              >
                Standard
              </button>
              <button
                type="button"
                className="list-mode-button"
                aria-pressed={listRenderMode === 'virtualized'}
                onClick={() => handleListRenderModeChange('virtualized')}
              >
                Virtualized
              </button>
            </div>
          </div>
        )}
        <ViewModeToggle viewMode={viewMode} onChange={handleViewModeChange} />
      </div>

      {/* Bulk Actions Bar */}
      {viewMode === 'list' && selectedTasks.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-actions-info">
            <strong>{selectedTasks.size}</strong> task(s) selected
          </div>
          <div className="bulk-actions-buttons">
            <select
              className="bulk-status-select"
              onChange={(e) => {
                if (e.target.value) {
                  handleBulkStatusUpdate(e.target.value as TaskStatus);
                  e.target.value = ''; // Reset select
                }
              }}
              defaultValue=""
            >
              <option value="" disabled>Change Status...</option>
              <option value="TODO">TODO</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="DONE">Done</option>
            </select>
            <button 
              className="btn btn-danger"
              onClick={handleBulkDelete}
            >
              Delete Selected
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => setSelectedTasks(new Set())}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Task Table */}
      {loading && tasks.length === 0 ? (
        <TaskListSkeleton />
      ) : (
        <>
          {viewMode === 'board' ? (
            <TaskBoard
              tasks={tasks}
              onDelete={handleDeleteTask}
              onStatusChange={handleStatusChange}
              onReorder={handleReorderTask}
              onViewDetails={(task) => void openTaskDetails(task)}
              onEdit={handleOpenEditModal}
              onOpenContextMenu={openTaskContextMenu}
            />
          ) : (
            <>
              {isVirtualizedListMode ? (
                <TaskVirtualizedList
                  tasks={tasks}
                  onDelete={handleDeleteTask}
                  onStatusChange={handleStatusChange}
                  onViewDetails={(task) => void openTaskDetails(task)}
                  onEdit={handleOpenEditModal}
                  onOpenContextMenu={openTaskContextMenu}
                  selectedTasks={selectedTasks}
                  onSelectTask={handleSelectTask}
                  onSelectAll={handleSelectAll}
                  canLoadMore={isInfiniteListMode && hasMoreTasks}
                  loadingMoreTasks={loadingMoreTasks}
                  onReachListEnd={isInfiniteListMode ? handleLoadMoreTasks : undefined}
                />
              ) : (
                <TaskTable
                  tasks={tasks}
                  onDelete={handleDeleteTask}
                  onStatusChange={handleStatusChange}
                  onViewDetails={(task) => void openTaskDetails(task)}
                  onEdit={handleOpenEditModal}
                  onOpenContextMenu={openTaskContextMenu}
                  selectedTasks={selectedTasks}
                  onSelectTask={handleSelectTask}
                  onSelectAll={handleSelectAll}
                />
              )}

              {/* Mobile Card View */}
              <div className="task-cards-container">
                {tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onDelete={handleDeleteTask}
                    onStatusChange={handleStatusChange}
                    onViewDetails={(taskToView) => void openTaskDetails(taskToView)}
                    onEdit={handleOpenEditModal}
                  />
                ))}
              </div>

              {tasks.length === 0 && !loading && (
                <div className="empty-state">
                  <h3>No tasks found</h3>
                  <p>Click &quot;Add New Task&quot; to create one!</p>
                </div>
              )}

              {isInfiniteListMode && tasks.length > 0 && (
                <div className="task-infinite-status" aria-live="polite">
                  <span>
                    Showing {tasks.length} of {totalElements} tasks
                  </span>
                  {loadingMoreTasks ? <span>Loading more tasks...</span> : null}
                  {!loadingMoreTasks && hasMoreTasks ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleLoadMoreTasks}
                    >
                      Load more
                    </button>
                  ) : null}
                  {!hasMoreTasks ? <span>All matching tasks loaded.</span> : null}
                  <div
                    ref={infiniteScrollSentinelRef}
                    className="task-infinite-sentinel"
                    aria-hidden="true"
                    data-testid="task-infinite-sentinel"
                  />
                </div>
              )}
            </>
          )}

          {/* Pagination Controls */}
          {viewMode === 'list' && listDataMode === 'paged' && totalElements > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalElements={totalElements}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default TaskManagerPage;
